import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { apiUrl } from '@/api/axios';
import API_ENDPOINTS from '@/api/apiConfig';
import { handleResponseError } from '@/core/utils/handleResponseError';
import { getTranslation } from '@/core/utils/i18nHelper';
import type {
	AvatarUploadResponse,
	ProfileAvatarState,
} from '@/store/types/profileAvatarTypes';
import { buildAvatarFormData } from '@/store/types/profileAvatarTypes';
import type { AppDispatch } from '@/store/store';

/**
 * Состояние аватара стартует пустым: компонент показывает SVG-фоллбек,
 * пока не придёт `url` c `GET /api/profile/avatar` или после аплоада.
 */
const initialState: ProfileAvatarState = {
	url: null,
	avatarId: null,
	isUploading: false,
	isDeleting: false,
	successMessage: null,
	error: null,
};

const slice = createSlice({
	name: 'profileAvatar',
	initialState,
	reducers: {
		setAvatarUrl(state, action: PayloadAction<string | null>) {
			state.url = action.payload;
		},
		setAvatarId(state, action: PayloadAction<number>) {
			state.avatarId = action.payload;
		},
		uploadStart(state) {
			state.isUploading = true;
			state.error = null;
			state.successMessage = null;
		},
		uploadSuccess(state, action: PayloadAction<{ url: string; successMessage: string }>) {
			state.url = action.payload.url;
			state.isUploading = false;
			state.successMessage = action.payload.successMessage;
			state.error = null;
		},
		uploadError(state, action: PayloadAction<string>) {
			state.error = action.payload;
			state.isUploading = false;
		},
		deleteStart(state) {
			state.isDeleting = true;
			state.error = null;
			state.successMessage = null;
		},
		deleteSuccess(state, action: PayloadAction<string>) {
			state.url = null;
			state.isDeleting = false;
			state.successMessage = action.payload;
			state.error = null;
		},
		deleteError(state, action: PayloadAction<string>) {
			state.error = action.payload;
			state.isDeleting = false;
		},
		resetSuccessMessage(state) {
			state.successMessage = null;
		},
	},
});

export const {
	setAvatarUrl,
	setAvatarId,
	uploadStart,
	uploadSuccess,
	uploadError,
	deleteStart,
	deleteSuccess,
	deleteError,
	resetSuccessMessage,
} = slice.actions;

/**
 * Загружает текущий URL аватара через GET /api/profile/avatar.
 * Вызывается один раз при монтировании страницы профиля.
 */
export const loadAvatar = () => async (dispatch: AppDispatch) => {
	try {
		const { data } = await apiUrl.get<{ url: string | null; avatar_id: number }>(API_ENDPOINTS.profileAvatar);
		dispatch(setAvatarUrl(data.url));
		dispatch(setAvatarId(data.avatar_id));
	} catch {
		// Non-critical: component renders placeholder when userId is set, SVG fallback otherwise.
	}
};

/**
 * Загружает аватар через POST /api/profile/avatar (multipart/form-data).
 * Оптимистично: сразу показывает blob-превью, затем подтверждает CDN-URL.
 */
export const uploadAvatar = (file: File) => async (dispatch: AppDispatch) => {
	const previewUrl = URL.createObjectURL(file);
	dispatch(setAvatarUrl(previewUrl));
	dispatch(uploadStart());

	try {
		const res = await apiUrl.post<AvatarUploadResponse>(
			API_ENDPOINTS.profileAvatar,
			buildAvatarFormData(file),
			{ headers: { 'Content-Type': 'multipart/form-data' } },
		);

		const successMessage = getTranslation('profile.avatar.success_message') as string;
		dispatch(uploadSuccess({ url: res.data.url, successMessage }));
	} catch (error) {
		// Revert optimistic preview on failure.
		dispatch(setAvatarUrl(null));
		handleResponseError(error, (errorMessage: string) => dispatch(uploadError(errorMessage)));
	}
};

/**
 * Удаляет аватар через DELETE /api/profile/avatar.
 */
export const deleteAvatar = () => async (dispatch: AppDispatch) => {
	dispatch(deleteStart());

	try {
		await apiUrl.delete(API_ENDPOINTS.profileAvatar);
		const successMessage = getTranslation('profile.avatar.deleted_success') as string;
		dispatch(deleteSuccess(successMessage));
	} catch (error) {
		handleResponseError(error, (errorMessage: string) => dispatch(deleteError(errorMessage)));
	}
};

export default slice.reducer;
export { slice as profileAvatarSlice };
